import * as React from "react";
import { LoginPage } from "../../components/pages/login";
import { LoginMFAVerifyPage } from "../../components/pages/login/mfa-verify";
import { post } from "../../lib/request";
import { useNavigate } from "react-router-dom";
import { LoginRequest, LoginResponse } from "../../../../server/types/request/login";
import { ErrorResponse } from "../../../../server/types/request";
import { AxiosError } from "axios";
import { endpoint } from "../../../../server/router/endpoint";
import { NoMenuNavigationBar } from "../../components/parts/nomenu-navigation-bar";
import { clientEndpoint } from "../../routes/endpoint";
import { ErrorObject } from "../../../../server/types/error";
import { VerifyMFARequest, VerifyMFAResponse } from "../../../../shared/mfa";

const mfaVerifyFailedMessage = "認証に失敗しました。コードを確認して、もう一度お試しください。";

export const LoginContainter = () => {
	const navigate = useNavigate();
	const [error, setError] = React.useState<string>();
	const [errors, setErrors] = React.useState<ErrorObject>();
	const [mfaToken, setMfaToken] = React.useState<string>();
	const [mfaError, setMfaError] = React.useState<string>();
	const [isMfaPending, setIsMfaPending] = React.useState<boolean>(false);
	const onLogin = React.useCallback(
		(email: string, password: string) => {
			const request: LoginRequest = { email, password };
			post<LoginResponse>(endpoint.login, request)
				.then((response) => {
					const { data } = response;
					setError(undefined);
					setErrors(undefined);
					if (data.status === "authenticated") {
						navigate(data.redirectUrl || "/");
					}
					// MFAセットアップ済み: 認証アプリのコードを入力
					if (data.status === "mfaRequired") {
						setMfaToken(data.mfaToken);
					}
					// MFA未セットアップ: メール認証 → セットアップ
					if (data.status === "mfaSetupRequired") {
						navigate(clientEndpoint.mfaVerificationChallenge, {
							replace: true,
							state: { email, token: data.token },
						});
					}
					if (data.status === "verificationChallengeRequired") {
						navigate(clientEndpoint.loginVerify, { replace: true, state: { email, token: data.token } });
					}
				})
				.catch((error: AxiosError<ErrorResponse>) => {
					setError(error.response.data.message);
					setErrors(error.response.data.errors);
				});
		},
		[navigate],
	);
	const onVerifyMFA = React.useCallback(
		(code: string, recovery: boolean) => {
			const request: VerifyMFARequest = { code, mfaToken, recovery };
			setIsMfaPending(true);
			return post<VerifyMFAResponse>(endpoint.mfaVerify, request)
				.then((response) => {
					const { data } = response;
					if (data.status === "authenticated") {
						navigate(data.redirectUrl || "/");
						return;
					}
					// 最終ログインから一定期間経過している場合は、追加のメール認証
					navigate(clientEndpoint.loginVerify, {
						replace: true,
						state: { email: data.email, token: data.token },
					});
				})
				.catch((error: AxiosError<ErrorResponse>) => {
					// コード誤り・ロックなどはサーバのメッセージを表示する
					setMfaError(error.response?.data?.message ?? mfaVerifyFailedMessage);
					throw error;
				})
				.finally(() => setIsMfaPending(false));
		},
		[mfaToken, navigate],
	);
	const onChangeMfaMode = React.useCallback(() => setMfaError(undefined), []);
	const onPasswordChangePage = React.useCallback(() => {
		navigate(clientEndpoint.passwordResetMail);
	}, [navigate]);
	return (
		<>
			<NoMenuNavigationBar />
			{mfaToken ? (
				<LoginMFAVerifyPage
					error={mfaError}
					isPending={isMfaPending}
					onChangeMode={onChangeMfaMode}
					onSubmit={onVerifyMFA}
				/>
			) : (
				<LoginPage onLogin={onLogin} error={error} errors={errors} onPasswordChangePage={onPasswordChangePage} />
			)}
		</>
	);
};
